import React, { useEffect, useState } from "react";
import {
  Form,
  Popconfirm,
  Radio,
  InputNumber,
  Select,
  Button,
  message,
  DatePicker,
  Input,
  Space,
} from "antd";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { printBill } from "../../../../components/Receptionist/receptionKeys";
import PaymentModeSelector from "./PaymentModeSelector";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import {
  addPaymentApi,
  generateFinalChargesApi,
  generateIpBillApi,
  shareBillApi,
} from "../../../../services/receptionist.services";
import useQuery from "../../../../hooks/useQuery";
import { formatToINR } from "../../../../helpers/utility";
import {
  createCashAmountRule,
  syncCashAmountValidation,
  validateCashPaymentAmount,
} from "../../../../helpers/paymentValidation";
import dayjs from "dayjs";
import styles from "./BillingIp.module.css";
import generateIcon from "../../../../assets/receptionistIcons/ipBilling/generate.svg";

const priceTagList = [
  { name: "₹", id: false },
  { name: "%", id: true },
];

const PaymentModeField = ({ value, onChange, form }) => (
  <PaymentModeSelector
    value={value}
    onChange={(mode) => {
      onChange?.(mode);
      syncCashAmountValidation(form, mode, "amount");
    }}
  />
);

const FinalSettlement = ({ billData, refreshData }) => {
  const params = useQuery();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
 
  const [paymentChoice, setPaymentChoice] = useState(null);
  const partialPayment = paymentChoice === "partial";
  const continueWithDue = paymentChoice === "due";

  /* The discount is entered before the choice is made, so the choice must
     leave it alone - clearing it here wiped the figure the moment a radio was
     picked. */
  const [partialPaymentAmount, setPartialPaymentAmount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [date, setDate] = useState(() => {
    const admission = billData?.data?.admission;
    if (!admission?.is_active && admission?.discharge_date) {
      return dayjs(admission.discharge_date);
    }
    return dayjs().startOf("minute");
  });
  const [totalCharges, setTotalCharges] = useState(0);
  const [isPercent, setIsPercent] = useState(false);
  const [dueAmount, setDueAmount] = useState(0);
  const paymentMode = Form.useWatch("mode", form);
  const amountValue = Form.useWatch("amount", form);

  const activePatient = billData?.data?.admission?.is_active;
  const savedDischargeDateValue = billData?.data?.admission?.discharge_date;
  const savedDischargeDate = savedDischargeDateValue
    ? dayjs(savedDischargeDateValue)
    : null;
  const paymentDetails = billData?.data?.payment_details;
  const normalizedCharges = totalCharges?.data ?? totalCharges;
  const chargesData =
    !activePatient && paymentDetails
      ? paymentDetails
      : normalizedCharges || paymentDetails || {};
  const dueAmountTotal = Number(chargesData?.total_due_amount || 0);
  const hasDueAmount = dueAmountTotal > 0;
  const settledDiscount = Number(chargesData?.total_discount || 0);
  const showSettledDiscount = settledDiscount > 0;
  const allowDiscount = activePatient && hasDueAmount;
  const allowPartialPayment = hasDueAmount;

  const getSubmitLabel = () => {
    if (partialPayment || (!activePatient && hasDueAmount)) {
      return "Generate receipt";
    }
    return "Generate bill";
  };

  useEffect(() => {
    if (paymentMode) {
      form.validateFields(["amount"]).catch(() => {});
    }
  }, [paymentMode, form]);

  useEffect(() => {
    if (!activePatient) {
      form.setFieldsValue({ discount: undefined, is_percent: false });
    }
  }, [activePatient, form]);

  const billId = billData?.data?.bill?.id;

  const downLoadBill = () => {
    shareBillApi(billId, `type=bill&share=download`)
      .then((res) => {
        const s3Link = res?.data?.data;
        if (s3Link) {
          window.open(s3Link, "_blank");
        } else {
          message.error("Failed to retrieve download link.");
        }
      })
      .catch(() => message.error("Error downloading the bill."));
  };

  const shareBill = () => {
    shareBillApi(billId, `type=bill&share=email`)
      .then(() => message.success("Bill Shared Successfully"))
      .catch((err) => message.error(err?.response?.data?.message));
  };

  const resolveDiscountInRupees = (discount, isPercentMode) => {
    if (!discount) return 0;
    if (isPercentMode) {
      const totalAmount = chargesData?.total_amount || 0;
      return Number(((discount * totalAmount) / 100).toFixed(2));
    }
    return Number(discount);
  };

  const onFinish = (values) => {
    
    const payingAmount = continueWithDue ? 0 : Number(values?.amount ?? 0);
    const isZeroPayment = !payingAmount;

    if (!isZeroPayment) {
      const cashError = validateCashPaymentAmount(payingAmount, values?.mode);
      if (cashError) {
        message.error(cashError);
        return;
      }
    }

    const discountInRupees = allowDiscount
      ? resolveDiscountInRupees(values?.discount, values?.is_percent)
      : 0;

    const formData = {
      amount: payingAmount,
      discount: discountInRupees,
      mode: isZeroPayment ? undefined : values?.mode,
      discharge_date: values?.discharge_date,
      transaction_number: isZeroPayment ? undefined : values?.transaction_number,
      remarks: isZeroPayment ? undefined : values?.remarks,
    };

    const isPartialReceipt =
      partialPayment ||
      Number(values?.amount) < Math.max(0, dueAmountTotal - discountInRupees);

    setLoading(true);
    activePatient
      ? generateIpBillApi(params?.patient, formData)
          .then((res) => {
            message.success(res.data.message);
            printBill(
              res?.data?.data?.id,
              res?.data?.data?.payments?.[0]?.id,
              isPartialReceipt
            );
            form.resetFields();
            refreshData();
            setLoading(false);
          })
          .catch((err) => {
            message.error(err.response.data.message);
            setLoading(false);
          })
      : addPaymentApi(billData?.data?.bill?.id, formData)
          .then((res) => {
            message.success(res.data.message);
            form.resetFields();
            refreshData();
            printBill(
              res?.data?.data?.bill,
              res?.data?.data?.id,
              isPartialReceipt
            );
            setLoading(false);
          })
          .catch((err) => {
            message.error(err.response.data.message);
            setLoading(false);
          });
  };

  useEffect(() => {
    if (!allowPartialPayment) return;

    const values = form.getFieldsValue();
    const due = dueAmountTotal;

    const discountInRupees = allowDiscount
      ? resolveDiscountInRupees(values?.discount, values?.is_percent)
      : 0;

    const payable = due - discountInRupees < 0 ? 0 : due - discountInRupees;

    /* Changing the choice re-bases the box on what is payable, so the figure
       on screen is always the bill less the discount. A partial payment is
       then typed down from it. */
    form.setFieldsValue({ amount: payable });
    setFinalAmount(payable);
    setPartialPaymentAmount(payable);
    setDueAmount(continueWithDue ? payable : 0);
  }, [
    partialPayment,
    continueWithDue,
    allowPartialPayment,
    allowDiscount,
    dueAmountTotal,
    form,
  ]);

  const onValuesChange = (changedValues, allValues) => {
    const { discount, is_percent } = { ...changedValues, ...allValues };
    const due = dueAmountTotal;

    if (allowDiscount && changedValues?.is_percent !== undefined) {
      setIsPercent(changedValues.is_percent);
      form.setFields([{ name: "discount", errors: [] }]);
      form.validateFields(["discount"]);
    }

    const discountInRupees = allowDiscount
      ? resolveDiscountInRupees(discount, is_percent)
      : 0;

    /* The discount comes off the bill, so what is left to pay is the due
       less the discount. A partial payment takes some of that and leaves the
       rest outstanding; every other choice settles it in full.
       The box is re-based whenever the discount moves, but never while the
       receptionist is typing into it. */
    const payable = Math.max(0, Number((due - discountInRupees).toFixed(2)));
    const typedAmount = changedValues?.amount !== undefined;

    if (allowPartialPayment && partialPayment) {
      const collected = typedAmount ? Number(changedValues.amount || 0) : payable;
      if (!typedAmount) form.setFieldsValue({ amount: payable });
      setFinalAmount(collected);
      setPartialPaymentAmount(collected);
      setDueAmount(Math.max(0, Number((payable - collected).toFixed(2))));
    } else {
      form.setFieldsValue({ amount: payable });
      setFinalAmount(payable);
      setPartialPaymentAmount(payable);
      setDueAmount(continueWithDue ? payable : 0);
    }
  };

  useEffect(() => {
    if (dueAmountTotal) {
      form.setFieldsValue({
        amount: dueAmountTotal > 0 ? dueAmountTotal.toFixed(2) : 0,
      });
      setPartialPaymentAmount(Number(dueAmountTotal.toFixed(2)));
      setFinalAmount(dueAmountTotal > 0 ? dueAmountTotal.toFixed(2) : 0);
    }
  }, [billData, dueAmountTotal, form]);

  useEffect(() => {
    if (!activePatient && savedDischargeDateValue) {
      const lockedDate = dayjs(savedDischargeDateValue);
      setDate(lockedDate);
      form.setFieldsValue({ discharge_date: lockedDate });
    }
  }, [activePatient, savedDischargeDateValue, form]);

  useEffect(() => {
    generateFinalChargesApi(params?.patient, { discharge_date: date })
      ?.then((res) => {
        setTotalCharges(res?.data?.data ?? res?.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [billData, date]);

  return (
    <Form
      layout="vertical"
      onFinish={onFinish}
      form={form}
      onValuesChange={onValuesChange}
      initialValues={{
        discharge_date:
          !activePatient && savedDischargeDate
            ? savedDischargeDate
            : dayjs().startOf("minute"),
        is_percent: false,
      }}
    >
      <Form.Item label="Discharge date and time" name="discharge_date">
        <DatePicker
          showTime={{ format: "HH:mm" }}
          format="YYYY-MM-DD HH:mm"
          className={inputStyles.mdDateInput}
          disabled={!activePatient}
          inputReadOnly={!activePatient}
          onChange={(selectedDate) => {
            if (activePatient && selectedDate) {
              setDate(selectedDate);
            }
          }}
        />
      </Form.Item>

      <div className={styles.paymentSummaryList}>
        <div className={styles.paymentSummaryRow}>
          <span>Total charges</span>
          <strong>{formatToINR(chargesData?.total_amount?.toFixed(2))}</strong>
        </div>
        <div className={styles.paymentSummaryRow}>
          <span>Payments</span>
          <strong>
            {formatToINR(chargesData?.total_paid_amount?.toFixed(2))}
          </strong>
        </div>
        {showSettledDiscount && (
          <div className={styles.paymentSummaryRow}>
            <span>Discount</span>
            <strong>{formatToINR(settledDiscount.toFixed(2))}</strong>
          </div>
        )}
      </div>

      {hasDueAmount && (
        <>
          {/* The discount is settled first: it decides what is left to pay,
              and only then is there a choice about how much of that to take
              now. The amount below is what remains after it. */}
          {allowDiscount && (
            <>
              <Form.Item label="Discount" className={styles.discountField}>
                <Space.Compact block className={styles.discountCompact}>
                  <Form.Item
                    name="discount"
                    noStyle
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();

                          const isPercentMode = form.getFieldValue("is_percent");

                          if (isPercentMode) {
                            if (Number(value) > 100) {
                              return Promise.reject(
                                "Discount cannot be greater than 100%"
                              );
                            }
                            const totalAmount = chargesData?.total_amount || 0;
                            const discountInRupees =
                              (Number(value) * totalAmount) / 100;
                            if (discountInRupees > dueAmountTotal) {
                              return Promise.reject(
                                "Discount cannot be greater than due amount"
                              );
                            }
                          } else if (Number(value) > dueAmountTotal) {
                            return Promise.reject(
                              "Discount cannot be greater than amount"
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      onChange={() => {
                        form.setFields([{ name: "amount", errors: [] }]);
                        form.validateFields(["amount"]);
                      }}
                      min={0}
                      controls={false}
                      placeholder="0"
                      className={inputStyles.mdNumberInput}
                    />
                  </Form.Item>
                  <Form.Item name="is_percent" noStyle>
                    <Select
                      className={inputStyles.mdSelect}
                      onChange={() => {
                        form.setFields([{ name: "discount", errors: [] }]);
                        form.validateFields(["discount"]);
                      }}
                    >
                      {priceTagList.map((item) => (
                        <Select.Option key={String(item.id)} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </>
          )}

          {allowPartialPayment && (
            <Form.Item className={styles.partialCheckbox}>
              <Radio.Group
                value={paymentChoice}
                onChange={(e) => setPaymentChoice(e.target.value)}
              >
                <Space direction="vertical">
                  <Radio
                    value="partial"
                    onClick={() =>
                      paymentChoice === "partial" && setPaymentChoice(null)
                    }
                  >
                    Partial payment
                  </Radio>
                  <Radio
                    value="due"
                    onClick={() =>
                      paymentChoice === "due" && setPaymentChoice(null)
                    }
                  >
                    Continue with Due Amount
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          )}

          {!continueWithDue && (
          <Form.Item
            label="Amount to be paid"
            name="amount"
            className={styles.amountFormItem}
            rules={[createCashAmountRule(form, "mode")]}
          >
            <InputNumber
              addonBefore="₹"
              controls={false}
              disabled={!allowPartialPayment || !partialPayment}
              placeholder="0.00"
              className={`${inputStyles.mdNumberInput} ${styles.settlementAmountInput}`}
            />
          </Form.Item>

          )}

          {allowPartialPayment && partialPayment && (
            <p className={styles.dueHint}>Due amount: ₹ {dueAmount}</p>
          )}

          {continueWithDue && (
            <p className={styles.dueHint}>
              No payment will be collected now. ₹ {dueAmount} stays outstanding.
            </p>
          )}
        </>
      )}


      {/* Nothing to collect means nothing to collect it by. Continuing with
          the due is one way to get here; a discount that covers the whole
          balance is another, and that one still asked for a payment mode it
          could not use. */}
      {hasDueAmount && !continueWithDue && Number(amountValue || 0) > 0 && (
        <>
          <label className={styles.formLabel}>Payment mode</label>
          <Form.Item
            name="mode"
            rules={[
              { required: true, message: "Please select a payment mode" },
            ]}
          >
            <PaymentModeField form={form} />
          </Form.Item>

          {paymentMode && paymentMode !== "cash" && (
            <>
              <label className={styles.formLabel}>Transaction number</label>
              <Form.Item name="transaction_number">
                <Input
                  placeholder="Enter transaction ID"
                  className={inputStyles.mdInput}
                />
              </Form.Item>
            </>
          )}

          <label className={styles.formLabel}>Remarks</label>
          <Form.Item name="remarks" className={styles.remarksInput}>
            <Input.TextArea placeholder="Optional note" rows={2} />
          </Form.Item>
        </>
      )}

      {/*
        Always available. A settlement with nothing left to pay is still a
        settlement, so the bill must be generatable at a zero balance — this
        previously depended on a recomputed amount and so only appeared after
        the discharge date was touched.
      */}
      <div className={styles.settlementActions}>
        <Popconfirm
          title="Payment confirmation"
          description="Have you verified the payment details?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => form.submit()}
        >
          <Button
            loading={loading}
            type="primary"
            className={`${btnStyles.lgBtn} ${styles.submitBtn}`}
            icon={
              <img src={generateIcon} alt="" className={styles.submitBtnIcon} />
            }
          >
            {getSubmitLabel()}
          </Button>
        </Popconfirm>
      </div>

      {!activePatient && billId && (
        <div className={styles.dischargedBillActions}>
          <div className={styles.dischargedBillRow}>
            <Button
              type="primary"
              className={`${btnStyles.mdBtn} ${styles.dischargedBillBtn}`}
              onClick={downLoadBill}
            >
              Download Bill
            </Button>
            <Button
              type="primary"
              ghost
              className={`${btnStyles.mdBtn} ${styles.dischargedBillBtn}`}
              onClick={shareBill}
            >
              Share Bill
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default FinalSettlement;
